import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { getFields } from "../../../signup.fielddefs.ts";
import { FormCreator } from "@/components/shared/form-creator";
import { toast } from "sonner";
import { useSignup } from "@/app/auth/signup/signup.hooks.ts";
import { useWizard } from "@/app/auth/signup/signup.components/wizard/wizard.hooks.ts";
import { useNavigate } from "react-router";
import { organization_type } from "@/models/organization.model";

const teamMemberSchema = z
  .object({
    name: z.string().min(2),
    email: z.string().email(),
    phone: z.string(),
    password: z.string().min(8),
    confirm: z.string(),
  })
  .refine((v) => v.password === v.confirm, {
    path: ["confirm"],
    message: "Passwords must match",
  });

type TeamMemberValues = z.infer<typeof teamMemberSchema>;

export function TeamMemberInviteSignupForm({
  prefilledEmail,
  inviteToken
}: {
  prefilledEmail: string;
  inviteToken: string;
}) {
  const { saving, error, submit, needsVerification } = useSignup("orgInvite", inviteToken);
  const { inviterName, inviteeName } = useWizard();
  const navigate = useNavigate();

  const form = useForm<TeamMemberValues>({
    resolver: zodResolver(teamMemberSchema),
    defaultValues: {
      email: prefilledEmail,
      name: inviteeName ?? "",
      phone: "",
      password: "",
      confirm: "",
    },
  });

  const fields = getFields({
    showOrgType: false,
    showOrgName: false,
  });

  const onSubmit = async ({ name, email, phone, password }: TeamMemberValues) => {
    try {
      await submit({ name, email, phone, password, organizationType: organization_type.pmc });

      toast.success("Account created! Welcome to the team.");

      if (!needsVerification) navigate("/");
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Signup failed");
    }
  };

  return (
    <>
      <div className="flex flex-col items-start gap-2 text-center mb-6">
        <h1 className="text-2xl font-bold">Complete your account</h1>
        <p className="text-balance text-sm text-muted-foreground">
          {inviterName
            ? `You've been invited to join ${inviterName} as a team member`
            : "You've been invited to join as a team member"}
        </p>
      </div>

      {error && <p className="text-sm text-destructive mb-2">{error}</p>}
      <FormCreator
        form={form}
        formDefinition={fields}
        buttonText={saving ? "Creating…" : "Complete account"}
        onSubmit={onSubmit}
      />
    </>
  );
}
